import {parseISO} from "date-fns";
import _ from "lodash";
import React, {Fragment, useCallback, useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {useStorageState} from "react-storage-hooks";
import {BEAUFTRAGTER, BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBA, ZUST_SBB, ZUST_SBD, ZUST_SBS} from "../../consts/funktionaleRollen";
import routes from "../../consts/routes.json";
import {calculateBetriebLink} from "../../initialObjects/BetriebObjects";
import {
    getBetriebeEndpoint,
    postBetriebeExportAllEndpointConfiguration,
    postBetriebeExportEndpointConfiguration
} from "../../services/BetriebeService";
import {getBetriebsstatusEndpoint, getBetriebsTypEndpoint} from "../../services/WertelistenService";
import {handleApiException, useHttp} from "../../services/HttpService";
import {getOrganisationenEndpoint} from "../../services/OrganisationsService";
import "../../styles/components/betriebsArbeitsListe.css";
import {
    containsSelectAll,
    createCalculatedColumn,
    createColumn,
    createComponentColumn,
    createPaginatedSelectRowComponentColumn
} from "../../utils/ColumnUtils";
import {formatDate} from "../../utils/DateUtils";
import {useForm} from "../../utils/FormUtils";
import {changeFieldValueIfPresent} from "../../utils/ObjectUtils";
import {userHasAnyRole} from "../../utils/SecurityUtils";
import {toBase64QueryParam} from "../../utils/URLUtils";
import EAButton from "../EAButton";
import EAButtonBar from "../EAButtonBar";
import EAFieldset from "../EAFieldset";
import EATableActionButton from "../tableComponents/EATableActionButton";
import ErweiterteSuche from "./ErweiterteSuche";
import EAPaginatedTable from "../EAPaginatedTable";
import EADownloadButton from "../EADownloadButton";
import EASelectButton from "../EASelectButton";
import usePersonalisationAndPagination from "./usePersonalisationAndPagination";
import {getDefaultPageInfo, toQueryParams} from "../../utils/PageInfoUtils";
import {personalisierungEndpoint} from "../../services/AnwenderService";

const initialSearchParameters = {
    betriebsstatus: {
        id: "IN_BETRIEB",
        value: "in Betrieb"
    },
    pageInfo: getDefaultPageInfo("bezeichnung")
};

function BetriebsArbeitsListe({
                                  history,
                                  sharedFilter,
                                  sharedFormFactory,
                                  regionalstelleOptionen,
                                  raeumlichesKriteriumOptionen,
                                  behoerdeOptionen,
                                  onErweiterteSucheOpenChanged
                              }) {

    const id = "BetriebsArbeitsliste";
    const filterFieldId = "betriebsbezeichnung-filter";
    const attr = {
        organisation: "organisation",
        verwaltungseinheit: "verwaltungseinheit",
        wasserkoerper: "wasserkoerper",
        betriebsBezeichnung: "betriebsBezeichnung",
        betriebstyp: "betriebstyp",
        betriebsstatus: "betriebsstatus",

        regionalstelle: "regionalstelle",
        aktenzeichen: "aktenzeichen",
        bescheidsdatum: "bescheidsdatum",
        behoerde: "behoerde",

        gueltigVon: "gueltigVon",
        gueltigBis: "gueltigBis",
    };

    const [betriebsTypOptionen] = useHttp(getBetriebsTypEndpoint);
    const [betriebsstatusOptions] = useHttp(getBetriebsstatusEndpoint);
    const [organisationOptions] = useHttp(getOrganisationenEndpoint);

    const [personalisierungsfilter, refetchPersonalisierungsfilter] = useHttp(personalisierungEndpoint);
    const [betriebstypPersonalisiert, setBetriebstypPersonalisiert] = useState(false);
    const [raeumlichesKriteriumPersonalisiert, setRaeumlichesKriteriumPersonalisiert] = useState(false);

    useEffect(() => {
        let newBetriebstypValue = false;
        let newVerwaltungseinheitValue = false;

        if (personalisierungsfilter.data) {
            if (personalisierungsfilter.data.betriebstyp && sharedFilter.personalisiert) {
                newBetriebstypValue = true;
            }
            if (personalisierungsfilter.data.verwaltungseinheit && sharedFilter.personalisiert) {
                newVerwaltungseinheitValue = true;
            }
        }

        setBetriebstypPersonalisiert(newBetriebstypValue);
        setRaeumlichesKriteriumPersonalisiert(newVerwaltungseinheitValue);
    }, [personalisierungsfilter, sharedFilter.personalisiert]);

    useEffect(() => {
        refetchPersonalisierungsfilter().then();
    }, [sharedFilter.personalisiert, refetchPersonalisierungsfilter]);

    const [selected, setSelected] = useStorageState(sessionStorage, "eab-selected-betriebe", []);
    const [storedFilter, setStoredFilter] = useStorageState(sessionStorage, "eab-betriebe-filter", initialSearchParameters);

    const {values: filter, setValues: setFilter, formFactory} = useForm({initialValues: storedFilter});

    const createQuery = useCallback((filterObject) => {
        let f = _.cloneDeep(filterObject);

        if (!f.extendedSearchOpen) {
            delete f.raeumlich;
            delete f.gueltigVon;
            delete f.gueltigBis;
            delete f.regionalstelle;
            delete f.aktenzeichen;
            delete f.bescheidsdatum;
            delete f.behoerde;
        }
        changeFieldValueIfPresent(f, attr.organisation, "id");
        changeFieldValueIfPresent(f, attr.betriebstyp, "id");
        changeFieldValueIfPresent(f, attr.betriebsstatus, "id");
        changeFieldValueIfPresent(f, attr.regionalstelle, "id");
        changeFieldValueIfPresent(f, attr.behoerde, "id");

        if (f.raeumlich) {
            f[attr.verwaltungseinheit] = f["raeumlich"]["verwaltungseinheit"];
            f[attr.wasserkoerper] = f["raeumlich"]["wasserkoerper"];
            delete f.raeumlich;
        }

        if (!f.pageInfo) {
            f.pageInfo = getDefaultPageInfo("bezeichnung");
        }
        let pageInfoParams = toQueryParams(f.pageInfo);
        delete f.pageInfo;

        delete f.extendedSearchOpen;
        if (Object.keys(f).length === 0) {
            return pageInfoParams;
        }

        return "q=" + toBase64QueryParam(f) + "&" + pageInfoParams;
    }, [attr.behoerde, attr.betriebsstatus, attr.betriebstyp, attr.organisation, attr.regionalstelle, attr.verwaltungseinheit, attr.wasserkoerper]);

    const initialQuery = useRef(getBetriebeEndpoint(createQuery({...storedFilter, ...sharedFilter})));
    const [{data: betriebe, loading: betriebeLoading}, refetchBetriebe] = useHttp(initialQuery.current, {manual: true});
    const [initialized, setInitialized] = useState(false);

    const executeSearch = (storedFilterParam, sharedFilterParam) => {
        if (initialized) {
            refetchBetriebe(getBetriebeEndpoint(createQuery({...storedFilterParam, ...sharedFilterParam}))).catch((e) =>
                handleApiException(e, "Error loading Betriebsarbeitsliste")
            );
        }
        setInitialized(true);
    };

    usePersonalisationAndPagination(sharedFilter, storedFilter, executeSearch);

    function handleCreateItemClick(index) {
        history.push(routes.BETRIEBSAKTE_RAW + "/neu?modus=s&typ=" + betriebsTypOptionen.data[index].id);
    }

    function editAllowed() {
        return userHasAnyRole([SYSTEMADMIN, FACHADMIN, BETREIBER, BEAUFTRAGTER, ZUST_SBD, ZUST_SBB, ZUST_SBS, ZUST_SBA]);
    }

    const betriebsColumns = [
        createPaginatedSelectRowComponentColumn("betriebsArbeitsliste", "id", selected, setSelected),
        createComponentColumn(
            "actions",
            (row, index) => {
                return (
                    <div className="eatabwrapper">
                        {editAllowed() && (
                            <EATableActionButton id={"abs-" + index} iconName="ico-edit">
                                <Link to={calculateBetriebLink(row, "s")}/>
                            </EATableActionButton>
                        )}
                        <EATableActionButton id={"abl-" + index} iconName="ico-view">
                            <Link to={calculateBetriebLink(row)}/>
                        </EATableActionButton>
                    </div>
                );
            },
            "",
            null,
            {padding: 0, width: "50px", minWidth: "50px", maxWidth: "50px"}
        ),
        createColumn("bezeichnung", "Betrieb"),
        createColumn("betreiber", "Betreiber"),
        createColumn("betriebstyp", "Betriebstyp"),
        createColumn("betriebsstatus", "Status"),
    ];
    if ((sharedFilter.gueltigVon || sharedFilter.gueltigBis) && !sharedFilter.personalisiert) {
        betriebsColumns.push(
            createCalculatedColumn(
                "auditInfo.gueltigAb",
                (row) => (row?.auditInfo?.gueltigAb ? formatDate(parseISO(row.auditInfo.gueltigAb)) : null),
                "Gültig von"
            )
        );
        betriebsColumns.push(
            createCalculatedColumn(
                "auditInfo.gueltigBis",
                (row) => (row?.auditInfo?.gueltigBis ? formatDate(parseISO(row.auditInfo.gueltigBis)) : null),
                "Gültig bis"
            )
        );
    }

    return (
        <Fragment>
            <EAFieldset id={id} caption="Suche">
                <div className="simpleFilter gridl">
                    {formFactory.renderInput(
                        attr.betriebsBezeichnung,
                        filterFieldId + "-" + attr.betriebsBezeichnung,
                        "Betriebsbezeichnung"
                    )}
                    {formFactory.renderAutocomplete(
                        attr.organisation,
                        filterFieldId + "-" + attr.organisation,
                        "Organisation",
                        organisationOptions.data,
                        "bezeichnung",
                        "id",
                        organisationOptions.loading
                    )}
                    {personalisierungsfilter.data && formFactory.renderAutocompletePersonalisiert(
                        attr.betriebstyp,
                        filterFieldId + "-" + attr.betriebstyp,
                        "Betriebstyp",
                        betriebsTypOptionen.data,
                        "value",
                        "id",
                        betriebsTypOptionen.loading,
                        false,
                        false,
                        null,
                        null,
                        betriebstypPersonalisiert
                    )}
                    {formFactory.renderAutocomplete(
                        attr.betriebsstatus,
                        filterFieldId + "-" + attr.betriebsstatus,
                        "Betriebsstatus",
                        betriebsstatusOptions.data,
                        "value",
                        "id",
                        betriebsstatusOptions.loading
                    )}
                </div>
                <ErweiterteSuche
                    sharedFilter={sharedFilter}
                    onExtendedSearchOpenChanged={onErweiterteSucheOpenChanged}
                    sharedFormFactory={sharedFormFactory}
                    regionalstelleOptionen={regionalstelleOptionen}
                    raeumlichesKriteriumOptionen={raeumlichesKriteriumOptionen}
                    behoerdeOptionen={behoerdeOptionen}
                    raeumlichesKriteriumPersonalisiert={raeumlichesKriteriumPersonalisiert}
                />
                <EAButtonBar style={{marginTop: "var(--buttonBar-spacing-top)"}}>
                    <EAButton
                        id={filterFieldId + "-reset"}
                        label="Zurücksetzen"
                        onClick={() => {
                            setStoredFilter(initialSearchParameters);
                            setFilter(initialSearchParameters);
                            executeSearch(initialSearchParameters, sharedFilter);
                        }}
                    />
                    <EAButton id={filterFieldId + "-submit"} label="Suchen"
                              onClick={() => {
                                  let newStoredFilter = {...filter, pageInfo: {...storedFilter.pageInfo, page: 0}};
                                  setStoredFilter(newStoredFilter);
                                  setSelected([]);
                                  executeSearch(newStoredFilter, sharedFilter);
                              }}
                              color="primary"/>
                </EAButtonBar>
            </EAFieldset>
            <EAPaginatedTable
                id={id}
                loading={betriebeLoading}
                columns={betriebsColumns}
                data={betriebe}
                onDoubleClick={(row) => {
                    history.push(calculateBetriebLink(row));
                }}
                onAltDoubleClick={(row) => {
                    if (editAllowed()) {
                        history.push(calculateBetriebLink(row, "s"));
                    }
                }}
                pageInfo={storedFilter.pageInfo}
                setPageInfo={(pageInfo) => {
                    setStoredFilter({...storedFilter, pageInfo});
                }}
            />
            <EAButtonBar style={{marginTop: "var(--buttonBar-spacing-top)"}}>
                <EADownloadButton
                    id={"exportArbeitsliste"}
                    loading={betriebeLoading}
                    label={"Excel-Export"}
                    disabled={!selected || selected.length === 0}
                    config={containsSelectAll(selected) ?
                        postBetriebeExportAllEndpointConfiguration(createQuery({...storedFilter, ...sharedFilter}))
                        : postBetriebeExportEndpointConfiguration(selected)}
                    style={{marginRight: "auto"}}
                />
                {userHasAnyRole([ZUST_SBD, FACHADMIN, SYSTEMADMIN]) && (
                    <EASelectButton
                        id="betrieb-anlegen"
                        label="Betrieb anlegen"
                        options={
                            betriebsTypOptionen.data && betriebsTypOptionen.data.length > 0
                                ? betriebsTypOptionen.data.map((b) => b.value)
                                : []
                        }
                        onItemClick={handleCreateItemClick}
                        color="primary"
                    />
                )}
            </EAButtonBar>
        </Fragment>
    );
}

export default BetriebsArbeitsListe;
